<template>
  <div
    :ref="'chartdiv' + id"
    :id="'chartdiv' + id"
    style="width: 100%; background-color: #FFFFFF;"
    :style="'height: ' + height"></div>
</template>

<script>
import * as am4core from '@amcharts/amcharts4/core'
import * as am4charts from '@amcharts/amcharts4/charts'
import am4themesAnimated from '@amcharts/amcharts4/themes/animated'
import am4langFr from '@amcharts/amcharts4/lang/fr_FR'
import { throttle } from 'lodash'
am4core.useTheme(am4themesAnimated)

export default {
  name: 'StatsCharts',
  props: {
    entities: {
      type: Array,
      required: true
    },
    /**
       * @enum { 'bar', 'line' }
       */
    chartType: {
      type: String,
      default: 'line'
    },
    chartSmooth: Boolean,
    /**
       * @expect [{ name: String, key: String, color: String, fill: String }]
       */
    chartLineConfigs: {
      type: Array,
      required: true
    },
    chartConfig: Object,
    height: {
      type: String,
      default: '420px'
    }
  },
  data () {
    return {
      chart: null,
      line: {
        type: 'line',
        fillColors: '#FFFFFF',
        bullet: 'round'
      },
      id: Math.random().toString(16).slice(2, 8)
    }
  },
  watch: {
    entities () {
      this.init()
    }
  },
  mounted () {
    this.init()
  },
  beforeDestroy () {
    if (this.chart) {
      this.chart.dispose()
    }
  },
  methods: {
    init () {
      this.makeChart(this.entities)
    },
    makeChart: throttle(function (data) {
      let chart = am4core.create(this.$refs['chartdiv' + this.id], am4charts.XYChart)
      chart.logo.opacity = 0
      chart.language.locale = am4langFr
      chart.paddingRight = 20
      chart.data = data
      chart.cursor = new am4charts.XYCursor()
      chart.cursor.fill = '#fff'
      chart.legend = new am4charts.Legend()
      chart.legend.contentAlign = 'left'
      chart.legend.fontSize = '10px'
      if (this.chartConfig && this.chartConfig.legendPosition) {
        chart.legend.position = this.chartConfig.legendPosition
        if (this.chartConfig.legendPosition === 'top') {
          chart.legend.paddingBottom = '20px'
          chart.legend.paddingTop = '-20px'
        }
      }
      if (this.chartType === 'bar') {
        chart.legend.useDefaultMarker = true
        let marker = chart.legend.markers.template.children.getIndex(0)
        marker.cornerRadius(0, 0, 0, 0)
        let markerTemplate = chart.legend.markers.template
        markerTemplate.width = 35
        markerTemplate.height = 17
      }

      let dateAxis = chart.xAxes.push(new am4charts.DateAxis())
      dateAxis.renderer.grid.template.location = 0
      dateAxis.renderer.grid.template.strokeOpacity = 0.05
      dateAxis.renderer.minGridDistance = 1
      dateAxis.baseInterval = { timeUnit: 'month' }
      dateAxis.dateFormats.setKey('month', '[font-size: 10px; #cccccc]MMMM')
      dateAxis.periodChangeDateFormats.setKey('month', '[font-size: 10px; #cccccc]MMMM yyyy')

      let valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
      valueAxis.tooltip.disabled = true
      valueAxis.renderer.minWidth = 35
      valueAxis.renderer.grid.template.strokeOpacity = 0.05
      valueAxis.renderer.labels.template.text = '[font-size: 10px; #000]{valueY}'
      valueAxis.renderer.labels.template.fontSize = '10px'
      valueAxis.renderer.labels.template.fill = am4core.color('#cccccc')
      valueAxis.renderer.labels.template.numberFormatter.numberFormat = '[font-size: 10px; #cccccc]############'
      valueAxis.min = 0
      valueAxis.maxPrecision = 0

      this.chartLineConfigs.forEach(config => {
        let v
        if (this.chartType === 'bar') {
          v = chart.series.push(new am4charts.ColumnSeries())
        } else {
          v = chart.series.push(new am4charts.LineSeries())
        }
        if (config.name) {
          if (this.chartConfig && this.chartConfig.lowerCaseText) v.name = config.name
          else v.name = config.name.toUpperCase()
        }
        v.dataFields.dateX = 'date'
        v.dataFields.valueY = config.key
        v.tooltipText = '[font-size: 10px; #000]{name} : {valueY}'
        v.stroke = am4core.color(config.color)
        v.strokeWidth = 2
        v.fill = am4core.color(config.color)
        if (config.fillLine) v.fillOpacity = 0.5
        if (this.chartSmooth) v.tensionX = 0.75
        if (this.chartType === 'line') {
          let vBullet = v.bullets.push(new am4charts.CircleBullet())
          vBullet.circle.radius = 4
          if (this.chartConfig && this.chartConfig.bulletSize) {
            vBullet.circle.radius = this.chartConfig.bulletSize
          }
          if (config.coloredBullet) {
            vBullet.circle.fill = am4core.color(config.color)
          } else {
            vBullet.circle.fill = am4core.color('#FFF')
            vBullet.circle.stroke = am4core.color(config.color)
            vBullet.circle.strokeWidth = 2
          }
        }
        v.tooltip.getFillFromObject = false
        v.tooltip.background.fill = am4core.color('#fff')
        v.tooltip.background.stroke = am4core.color(config.color)
        v.tooltip.background.strokeWidth = 0.7
        v.tooltip.background.cornerRadius = 0
        v.tooltip.fontSize = '10px'
      })
      this.chart = chart
    }, 300)
  }
}
</script>
